export type EstimateRateType =
  | 'primer'
  | 'hydro'
  | 'acrylic'
  | 'weather_protection'
  | 'custom'
  | 'painting'
  | 'plaster'
  | 'sanding'
  | 'scraping'
  | 'labor_hour'
  | 'bath_hydro'
  | 'bath_tiles'
  | 'bath_water_point'
  | 'bath_electric_point'
  | 'bath_tile_removal'
  | 'bath_groove_chasing'
  | 'bath_demolition'
  | 'paving_excavation'
  | 'paving_spoil_removal'
  | 'paving_geotextile'
  | 'paving_base'
  | 'paving_compaction'
  | 'paving_bedding'
  | 'paving_laying'
  | 'paving_cutting'
  | 'paving_edges'
  | 'paving_joint_sand'
  | 'paving_final_compaction'
  | 'transport_flat';

export type EstimateRegion = 'warminsko-mazurskie' | 'default' | string;

export type EstimateRates = Partial<Record<EstimateRateType, number>>;

export interface RegionPricingConfig {
  multiplier: number;
  minimumVisitNet: number;
  label: string;
}

/**
 * Printed on the client offer, so it states the commercial rule and nothing else.
 * The client has no business reading who added the item or how the contractor's
 * costs work — that is the contractor's side of the deal.
 */
export const MINIMUM_VISIT_PRICING_NOTE =
  'Dla tej pozycji obowiązuje minimalna wartość usługi.';

export const SMALL_OPENINGS_PRICING_NOTE =
  'W szybkim obmiarze wpisuj tylko nietypowe otwory przeznaczone do odliczenia. Małych okien nie wpisujemy. Każda wpisana powierzchnia zostanie odliczona.';

export const LOCAL_RATES_OLSZTYN_2026 = {
  gladz_reczna: "50-60 zł/m2",
  gladz_agregat: "50-65 zł/m2",
  gladz_premium_LED: "70+ zł/m2",
  malowanie_2x: "28-35 zł/m2",
  gruntowanie: "10-12 zł/m2",
  uwagi: "Poprawki po deweloperze zwykle +10-15% do stawki bazowej."
};

export const DEFAULT_ESTIMATE_RATES: Record<EstimateRateType, number> = {
  primer: 10,
  hydro: 45,
  painting: 28,
  plaster: 55,
  sanding: 15,
  scraping: 12,
  labor_hour: 75,
  bath_hydro: 55,
  bath_tiles: 130,
  bath_water_point: 650,
  bath_electric_point: 150,
  bath_tile_removal: 45,
  bath_groove_chasing: 45,
  bath_demolition: 150,
  paving_excavation: 28,
  paving_spoil_removal: 90,
  paving_geotextile: 6,
  paving_base: 160,
  paving_compaction: 8,
  paving_bedding: 130,
  paving_laying: 95,
  paving_cutting: 18,
  paving_edges: 45,
  paving_joint_sand: 5,
  paving_final_compaction: 7,
  transport_flat: 300,
  acrylic: 8,
  weather_protection: 0,
  custom: 0,
};

export const REGION_ESTIMATE_RATES: Record<string, Partial<Record<EstimateRateType, number>>> = {
  'warminsko-mazurskie': {
    primer: 10,
    plaster: 55,
    sanding: 15,
    scraping: 12,
    painting: 28,
    labor_hour: 75,
    hydro: 45,
    acrylic: 8,
    bath_hydro: 55,
    bath_tiles: 130,
    bath_water_point: 650,
    bath_electric_point: 150,
    bath_tile_removal: 45,
    bath_groove_chasing: 45,
    bath_demolition: 150,
    paving_excavation: 28,
    paving_spoil_removal: 90,
    paving_geotextile: 6,
    paving_base: 160,
    paving_compaction: 8,
    paving_bedding: 130,
    paving_laying: 95,
    paving_cutting: 18,
    paving_edges: 45,
    paving_joint_sand: 5,
    paving_final_compaction: 7,
    transport_flat: 300,
  },
};

export const REGION_PRICING_CONFIG: Record<string, RegionPricingConfig> = {
  default: {
    multiplier: 1,
    minimumVisitNet: 300,
    label: 'Region standardowy',
  },
  mazowieckie: {
    multiplier: 1.05,
    minimumVisitNet: 350,
    label: 'Mazowieckie - standard / poza aglomeracja',
  },
  'mazowieckie:warszawa': {
    multiplier: 1.35,
    minimumVisitNet: 500,
    label: 'Warszawa',
  },
  warszawa: {
    multiplier: 1.35,
    minimumVisitNet: 500,
    label: 'Warszawa',
  },
  'mazowieckie:podwarszawskie': {
    multiplier: 1.2,
    minimumVisitNet: 400,
    label: 'Podwarszawskie',
  },
  podwarszawskie: {
    multiplier: 1.2,
    minimumVisitNet: 400,
    label: 'Podwarszawskie',
  },
  'warminsko-mazurskie': {
    multiplier: 1,
    minimumVisitNet: 300,
    label: 'Olsztyn / wieksze miasto',
  },
  olsztyn: {
    multiplier: 1,
    minimumVisitNet: 300,
    label: 'Olsztyn / wieksze miasto',
  },
  'warminsko-mazurskie:small-town': {
    multiplier: 0.88,
    minimumVisitNet: 280,
    label: 'Male miasto',
  },
  'warminsko-mazurskie:mazury-seasonal': {
    multiplier: 1.12,
    minimumVisitNet: 400,
    label: 'Mazury / sezon / dluzszy dojazd',
  },
};

export function resolveRegionPricingConfig(region?: EstimateRegion): RegionPricingConfig {
  const key = region?.toLowerCase().trim();
  return (key && REGION_PRICING_CONFIG[key]) || REGION_PRICING_CONFIG.default;
}

export function resolveEstimateRate(
  type: EstimateRateType,
  region?: EstimateRegion,
  userRates?: EstimateRates
): number {
  if (userRates?.[type] !== undefined) {
    return userRates[type];
  }

  const normalizedRegion = region?.toLowerCase().trim() ?? '';
  const configuredRate = REGION_ESTIMATE_RATES[normalizedRegion]?.[type];
  const baseRate = configuredRate ?? DEFAULT_ESTIMATE_RATES[type];
  const multiplier = configuredRate === undefined
    ? resolveRegionPricingConfig(normalizedRegion).multiplier
    : 1;

  return Math.round(baseRate * multiplier * 100) / 100;
}
